import type { IsoDateTime, Uuid } from '@/shared/types/domain'

/**
 * La arquitectura como DATA, no como dibujo (plan 2.7).
 *
 * Cada pieza es un registro con su stack, su repo, su hosting y sus accesos; el
 * diagrama se deriva de eso. Un `.png` se desactualiza el día que alguien mueve
 * la base de host y nadie se entera.
 */
export type ComponentKind =
  | 'frontend'
  | 'backend'
  | 'worker'
  | 'database'
  | 'cache'
  | 'queue'
  | 'storage'
  | 'auth'
  | 'cdn'
  | 'mobile'
  | 'external_service'

export type ComponentEnvironment = 'prod' | 'staging' | 'both'

export type LinkProtocol =
  'http' | 'grpc' | 'sql' | 'amqp' | 'redis' | 'webhook' | 's3' | 'smtp' | 'other'

export type RepositoryRole = 'frontend' | 'backend' | 'infra' | 'mobile' | 'shared' | 'docs'

export interface ProjectComponent {
  id: Uuid
  company_id: Uuid
  project_id: Uuid
  key: string
  name: string
  kind: ComponentKind
  tech: string | null
  version: string | null
  hosting: string | null
  environment: ComponentEnvironment
  url: string | null
  repository_id: Uuid | null
  access_id: Uuid | null
  document_id: Uuid | null
  notes_md: string | null
  /** `null` ⇒ nunca se arrastró: lo acomoda elkjs. */
  position_x: number | null
  position_y: number | null
  is_client_visible: boolean
}

export interface ComponentLink {
  id: Uuid
  project_id: Uuid
  from_component_id: Uuid
  to_component_id: Uuid
  label: string | null
  protocol: LinkProtocol
  /** Asíncrono: la flecha va punteada. */
  is_async: boolean
  notes: string | null
}

/** `sync_error` es jsonb: guardamos el mensaje y cuándo falló. */
export interface SyncError {
  message: string
  at?: IsoDateTime | null
}

export interface ProjectRepository {
  id: Uuid
  project_id: Uuid
  provider: 'github'
  owner: string
  name: string
  url: string
  default_branch: string
  role: RepositoryRole
  is_private: boolean
  last_commit_sha: string | null
  last_commit_at: IsoDateTime | null
  last_commit_author: string | null
  open_prs: number | null
  primary_language: string | null
  synced_at: IsoDateTime | null
  /** Última sincronización que SALIÓ BIEN. Con error, `synced_at` es el intento. */
  last_sync_ok_at: IsoDateTime | null
  sync_error: SyncError | null
}

export interface ArchitectureTemplate {
  key: string
  name: string
  description: string
  component_count: number
}

export interface ComponentPosition {
  id: Uuid
  position_x: number | null
  position_y: number | null
}

/**
 * Familias de color.
 *
 * Once `kind` con once colores serían once colores que nadie distingue. Se
 * agrupan en familias que se leen de un vistazo (lo que sirve el cliente, lo
 * que procesa, lo que guarda, lo que no es nuestro) y el ícono desambigua
 * dentro de la familia.
 */
export type KindFamily = 'ui' | 'service' | 'worker' | 'data' | 'cache' | 'edge' | 'external'

export const KIND_FAMILY: Record<ComponentKind, KindFamily> = {
  frontend: 'ui',
  mobile: 'ui',
  backend: 'service',
  worker: 'worker',
  queue: 'worker',
  database: 'data',
  storage: 'data',
  cache: 'cache',
  auth: 'edge',
  cdn: 'edge',
  external_service: 'external',
}

export const FAMILY_TOKEN: Record<KindFamily, string> = {
  ui: '--arch-ui',
  service: '--arch-service',
  worker: '--arch-worker',
  data: '--arch-data',
  cache: '--arch-cache',
  edge: '--arch-edge',
  external: '--arch-external',
}

/**
 * El `kind` es vocabulario NUESTRO, no texto del equipo: se traduce.
 *
 * El mapa devuelve la CLAVE del catálogo, no el texto — `t(KIND_KEY[kind])` —
 * porque un módulo de tipos no tiene idioma. El nombre de la pieza, en cambio,
 * lo escribe y renombra el equipo y viaja tal cual (ver `docs/i18n.md`).
 */
export const KIND_KEY: Record<ComponentKind, string> = {
  frontend: 'architecture.kind.frontend',
  backend: 'architecture.kind.backend',
  worker: 'architecture.kind.worker',
  database: 'architecture.kind.database',
  cache: 'architecture.kind.cache',
  queue: 'architecture.kind.queue',
  storage: 'architecture.kind.storage',
  auth: 'architecture.kind.auth',
  cdn: 'architecture.kind.cdn',
  mobile: 'architecture.kind.mobile',
  external_service: 'architecture.kind.external_service',
}

/**
 * Los mismos `kind` en español fijo, **sólo para las fixtures de `src/test/msw`**,
 * que arman respuestas del servidor y no tienen un `t` a mano. La interfaz usa
 * `KIND_KEY`; si estás escribiendo pantalla, ésta no es la que buscás.
 */
export const KIND_LABEL: Record<ComponentKind, string> = {
  frontend: 'Frontend',
  backend: 'Backend',
  worker: 'Worker',
  database: 'Base de datos',
  cache: 'Cache',
  queue: 'Cola',
  storage: 'Almacenamiento',
  auth: 'Auth',
  cdn: 'CDN',
  mobile: 'Mobile',
  external_service: 'Servicio externo',
}

export const ROLE_KEY: Record<RepositoryRole, string> = {
  frontend: 'architecture.role.frontend',
  backend: 'architecture.role.backend',
  infra: 'architecture.role.infra',
  mobile: 'architecture.role.mobile',
  shared: 'architecture.role.shared',
  docs: 'architecture.role.docs',
}

export const ENVIRONMENT_KEY: Record<ComponentEnvironment, string> = {
  prod: 'architecture.environment.prod',
  staging: 'architecture.environment.staging',
  both: 'architecture.environment.both',
}

/**
 * Los protocolos son nombres de estándares (`http`, `amqp`, `s3`): iguales en
 * los tres idiomas. Pasan igual por el catálogo porque `other` sí cambia, y
 * mezclar "un enum crudo salvo uno" es cómo se cuela un `otro` en inglés.
 */
export const PROTOCOL_KEY: Record<LinkProtocol, string> = {
  http: 'architecture.protocol.http',
  grpc: 'architecture.protocol.grpc',
  sql: 'architecture.protocol.sql',
  amqp: 'architecture.protocol.amqp',
  redis: 'architecture.protocol.redis',
  webhook: 'architecture.protocol.webhook',
  s3: 'architecture.protocol.s3',
  smtp: 'architecture.protocol.smtp',
  other: 'architecture.protocol.other',
}

/** El color de borde del nodo, como valor CSS listo para usar. */
export function kindColor(kind: ComponentKind): string {
  return `hsl(var(${FAMILY_TOKEN[KIND_FAMILY[kind]]}))`
}

/** Los servicios externos van punteados: no son nuestros. */
export function isExternal(kind: ComponentKind): boolean {
  return kind === 'external_service'
}
